import {
	AlertOutlined,
	CalendarOutlined,
	SignatureOutlined,
	ToolOutlined,
} from "@ant-design/icons"
import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { Image } from "components/Image/Image"
import { InputRTE } from "components/Input/InputRTE"
import { Space } from "components/Space/Space"
import { Tag } from "components/Tag/Tag"
import { useDisclosure } from "hooks/useDisclosure"
import { WeightIcon } from "icons/WeightIcon"
import {
	Patient,
	TimelineDeviceEvent,
	TimelineEscalation,
	TimelineItem,
	TimelineNote,
	TimelineScreening,
} from "interface/patientType"
import { WeightLabel } from "modules/dashboard/WeightLabel"
import { CreateEscalation } from "modules/escalations/CreateEscalation"
import { useFlag } from "modules/flags/flags"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router"
import { cn } from "styles/cn"
import { humanReadable } from "utils"
import { asDate, dateFormatter, dayjs, today } from "utils/date"
import { Footnote } from "./Footnote"
import { PatientTimelineNote } from "./PatientTimelineNote"
import { useNavigateToScreening } from "./useNavigateToScreening"

export const PatientTimeline = ({
	items,
	patient,
	isAnonymous,
}: {
	items: TimelineItem[]
	patient: Patient
	isAnonymous: boolean
}) => {
	const { scopeId = "" } = useParams()
	const { clinicalNotes, clinicalEscalations } = useFlag(
		"clinicalNotes",
		"clinicalEscalations",
	)
	const [timelineItems, setTimelineItems] = useState<TimelineItem[]>(items)
	const isEditing = useDisclosure<string>()
	const isAdding = useDisclosure()
	const itemIds = useMemo(() => timelineItems.map(getItemId), [timelineItems])
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)
	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) {
			return
		}

		setTimelineItems((prevItems) => {
			const oldIndex = prevItems.findIndex(
				(item) => getItemId(item) === active.id,
			)
			const newIndex = prevItems.findIndex(
				(item) => getItemId(item) === over.id,
			)

			if (oldIndex === -1 || newIndex === -1) {
				return prevItems
			}

			const reordered = arrayMove(prevItems, oldIndex, newIndex)
			const movedItem = reordered[newIndex]

			if (movedItem.itemType !== "timelineNote") {
				return reordered
			}

			const newDisplayDate = midwayDate(
				reordered[newIndex - 1]?.effectiveDate,
				reordered[newIndex + 1]?.effectiveDate,
				movedItem.effectiveDate,
			)

			const updatedMovedItem: TimelineItem<"timelineNote"> = {
				...movedItem,
				effectiveDate: newDisplayDate,
				data: {
					...movedItem.data,
					displayDate: newDisplayDate,
				},
			}

			reordered[newIndex] = updatedMovedItem
			return reordered
		})
	}, [])

	useEffect(() => {
		if (!isEditing.value) {
			setTimelineItems(items)
		}
	}, [isEditing.value, items])

	return (
		<div
			role="region"
			aria-label="Patient timeline"
			className="flex w-full flex-col gap-6 pb-6"
		>
			<div className="flex justify-between gap-2">
				<h3 className="font-headings text-xl">Timeline</h3>

				<Space.Compact>
					{clinicalNotes.enabled && !isAnonymous && (
						<Button
							disabled={isEditing.isOpen || isAdding.isOpen}
							icon={<SignatureOutlined aria-hidden />}
							onClick={isAdding.open}
						>
							Add note
						</Button>
					)}
					{clinicalEscalations.enabled && !isAnonymous && <CreateEscalation />}
				</Space.Compact>
			</div>

			<div className="grid grid-cols-1 grid-rows-1">
				<div className="col-start-1 row-start-1 ml-15 h-full w-px bg-grey-30" />

				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					modifiers={[restrictToVerticalAxis]}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						disabled={!clinicalNotes.enabled || isAnonymous}
						items={itemIds}
						strategy={verticalListSortingStrategy}
					>
						<ul
							className="col-start-1 row-start-1 flex flex-col"
							aria-label={`${timelineItems.length} timeline item(s)`}
						>
							{isAdding.isOpen && (
								<li
									aria-level={1}
									aria-label="timeline note now"
									className="relative shadow-secondary"
								>
									<PatientTimelineNote
										mode="create"
										note={{
											patientId: patient.id,
											displayDate: today().toISOString(),
										}}
										className="mt-0"
										onToggle={isAdding.toggle}
									/>
								</li>
							)}

							{timelineItems.map((item, index) => {
								const { itemType, effectiveDate, data } = item
								const itemId = getItemId(item)
								const isLast = index === timelineItems.length - 1
								const displayDate = midwayDate(
									effectiveDate,
									timelineItems[index + 1]?.effectiveDate,
								)
								const effectiveDateText = dateFormatter(effectiveDate, {
									hasYear: false,
								})
								const ariaLabel = (() => {
									let itemTypeLabel = ""
									if (itemType === "screening") {
										const { isReviewed } = data as TimelineScreening
										itemTypeLabel = `${!isReviewed ? "new " : ""}screening`
									} else if (itemType === "deviceEvent") {
										itemTypeLabel = "device event"
									} else if (itemType === "timelineNote") {
										itemTypeLabel = "timeline note"
									} else if (itemType === "escalation") {
										itemTypeLabel = "escalation"
									}
									return `${itemTypeLabel} at ${effectiveDateText}`
								})()

								if (!data) {
									return null
								}

								return (
									<SortableTimelineItem
										key={itemId}
										id={itemId}
										ariaLabel={ariaLabel}
										isDraggable={
											clinicalNotes.enabled &&
											!isAnonymous &&
											itemType === "timelineNote" &&
											isEditing.value === itemId
										}
									>
										{(dragHandleProps, dragHandleRef, isDragging) => (
											<>
												{itemType === "deviceEvent" && (
													<ItemDeviceEvent data={data as TimelineDeviceEvent} />
												)}
												{itemType === "screening" && (
													<ItemScreening
														data={data as TimelineScreening}
														patient={patient}
														isAnonymous={isAnonymous}
													/>
												)}
												{itemType === "timelineNote" && (
													<PatientTimelineNote
														mode="update"
														note={data as TimelineNote}
														enabled={
															(!isEditing.isOpen ||
																isEditing.value === itemId) &&
															!isAdding.isOpen
														}
														onOpen={() => isEditing.open(itemId)}
														onClose={isEditing.close}
														onCancel={() => {
															isEditing.close()
															setTimelineItems(items)
														}}
														dragHandleProps={dragHandleProps}
														dragHandleRef={dragHandleRef}
													/>
												)}
												{itemType === "escalation" && (
													<ItemEscalation
														scopeId={scopeId}
														patientId={patient.id}
														data={data as TimelineEscalation}
													/>
												)}

												{!isLast && !isDragging && (
													<PatientTimelineNote.Create
														patientId={patient.id}
														displayDate={displayDate}
														enabled={
															clinicalNotes.enabled &&
															!isAnonymous &&
															!isEditing.isOpen &&
															!isAdding.isOpen
														}
														onToggle={isEditing.toggle}
													/>
												)}
											</>
										)}
									</SortableTimelineItem>
								)
							})}
						</ul>
					</SortableContext>
				</DndContext>
			</div>
		</div>
	)
}

function SortableTimelineItem({
	id,
	isDraggable,
	ariaLabel,
	children,
}: {
	id: string
	isDraggable: boolean
	ariaLabel?: string
	children: (
		dragHandleProps?: Record<string, unknown>,
		dragHandleRef?: (element: HTMLElement | null) => void,
		isDragging?: boolean,
	) => React.ReactNode
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id,
		disabled: !isDraggable,
	})

	return (
		<li
			ref={setNodeRef}
			aria-level={1}
			aria-label={ariaLabel}
			className={cn(
				"relative shadow-secondary",
				isDragging && "z-50 opacity-80",
			)}
			style={{
				transform: transform
					? `translate3d(${transform.x}px, ${transform.y}px, 0)`
					: undefined,
				transition,
			}}
		>
			{children(
				isDraggable ? { ...attributes, ...listeners } : undefined,
				isDraggable ? setActivatorNodeRef : undefined,
				isDragging,
			)}
		</li>
	)
}

function ItemScreening({
	data,
	patient,
	isAnonymous,
}: {
	data: TimelineScreening
	patient: Patient
	isAnonymous: boolean
}) {
	const { clinicalNotes } = useFlag("clinicalNotes")
	const navigate = useNavigateToScreening()
	const timestamp = dateFormatter(data.capturedAt, {
		hasYear: false,
	})
	const weekday = dateFormatter(data.capturedAt, {
		onlyWeekday: true,
		needSimplify: true,
	})

	return (
		<div
			role="link"
			tabIndex={0}
			className="focus-outline @container flex cursor-pointer flex-col rounded-[20px] border border-mint-20 bg-white p-5 shadow-secondary hover:border-green-60"
			onClick={() => navigate(data.id)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					navigate(data.id)
				}
			}}
		>
			<div className="grid grid-cols-[120px_1fr] place-items-start gap-2 @2xl:grid-cols-[max-content_1fr_1fr] @4xl:place-items-center">
				<div className="row-span-2 grid place-content-center place-self-start @2xl:row-span-1">
					{!data.imageThumbS3Key && (
						<div className="flex items-center justify-center">
							<WeightIcon.Null className="size-20 text-navy-60" />
						</div>
					)}

					{data.imageThumbS3Key && (
						<Image
							src={data.imageThumbS3Key}
							alt={`Screening image at ${timestamp}`}
							className="size-20 rounded-2xl bg-grey-30 object-cover"
						/>
					)}
				</div>

				<div className="flex items-center gap-5">
					<div
						className={cn(
							"grid min-h-11 min-w-11 place-items-center rounded-xl bg-grey-10",
							!data.isReviewed && "bg-purple-0",
						)}
					>
						<CalendarOutlined
							aria-hidden
							className={cn(
								"text-2xl text-navy-60",
								!data.isReviewed && "text-purple-80",
							)}
						/>
					</div>
					<div className="flex flex-col items-start">
						<p className="flex items-center gap-2">
							<span className="text-lg font-bold">{weekday}</span>
							{!data.isReviewed && !isAnonymous && (
								<Tag variant="filled" color="blue">
									New
								</Tag>
							)}
						</p>
						<p className="text-lg text-navy-60">{timestamp}</p>
					</div>
				</div>

				<div className="-col-end-1 flex items-center justify-start gap-5">
					<div className="grid min-h-11 min-w-11 place-items-center rounded-xl bg-grey-10">
						<WeightIcon className="size-6 fill-navy-60" aria-hidden />
					</div>

					<WeightLabel
						fullName={patient.fullName}
						dataPoint={data}
						className="font-bold"
					/>
				</div>
			</div>
			{data.screeningNote && clinicalNotes.enabled && (
				<>
					<Divider variant="dashed" className="my-3 border-grey-20" />
					<div className="flex items-start gap-3">
						<SignatureOutlined aria-hidden className="text-2xl text-navy-60" />
						<div className="flex w-full flex-col gap-2">
							<p className="line-clamp-3 whitespace-pre-wrap">
								{data.screeningNote.observations}
							</p>
							<Footnote
								labels={data.screeningNote.labels?.map(humanReadable)}
								editedBy={`${data.screeningNote.lastEditedByName}, ${asDate(data.screeningNote.updatedAt).fromNow()}`}
								editedByTooltip={dateFormatter(data.screeningNote.updatedAt, {
									hasYear: false,
								})}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	)
}

function ItemDeviceEvent({ data }: { data: TimelineDeviceEvent }) {
	const team = data.fieldSupportTeam?.name ?? "[Unknown team]"
	const date = data.completedDate
		? asDate(data.completedDate).fromNow()
		: "[Unknown date]"

	return (
		<div className="flex items-start gap-4 rounded-[20px] border border-mint-20 bg-white p-5 shadow-secondary hover:border-green-60">
			<div className="grid size-11 shrink-0 place-content-center rounded-xl bg-green-20">
				<ToolOutlined aria-hidden className="text-2xl text-green-80" />
			</div>
			<div className="flex w-full flex-col gap-2">
				<div className="flex flex-col gap-1">
					<h4 className="font-bold">{data.visitType}</h4>
					<p className="whitespace-pre-wrap">{data.notes}</p>
				</div>
				<Footnote
					editedBy={`${team}, ${date}`}
					editedByTooltip={dateFormatter(data.completedDate, {
						hasYear: false,
					})}
				/>
			</div>
		</div>
	)
}

function ItemEscalation({
	data,
	patientId,
	scopeId,
}: {
	data: TimelineEscalation
	patientId: string
	scopeId: string
}) {
	const navigate = useNavigate()
	const navigateToScreening = useNavigateToScreening()
	const name = data.lastActivityByName ?? data.createdByName
	const date = data.lastActivityAt
		? dayjs.max([asDate(data.createdAt), asDate(data.lastActivityAt)])
		: asDate(data.createdAt)
	const isOpen = data.status === "OPEN"
	const escalationLink = `/${scopeId}/patients/${patientId}/escalations/${data.id}`

	return (
		<div
			className={cn(
				"focus-outline flex items-start gap-4 rounded-[20px] border border-mint-20 bg-white p-5 shadow-secondary hover:border-green-60",
				isOpen && "border-orange-60 hover:border-orange-80",
			)}
		>
			<Link
				aria-hidden
				tabIndex={-1}
				to={escalationLink}
				className="grid size-11 shrink-0 place-content-center rounded-xl bg-orange-0"
			>
				<AlertOutlined aria-hidden className="text-2xl text-orange-80" />
			</Link>
			<div className="flex w-full flex-col gap-3">
				<div
					className="flex cursor-pointer flex-col gap-1"
					onClick={(e) => {
						if (
							e.defaultPrevented ||
							e.metaKey ||
							e.ctrlKey ||
							e.shiftKey ||
							e.altKey ||
							(e.target as HTMLElement).closest("a") ||
							Boolean(window.getSelection()?.toString())
						) {
							return
						}
						navigate(escalationLink)
					}}
				>
					<Link
						to={escalationLink}
						className="focus-outline rounded-lg outline-offset-2!"
					>
						<h4 className="font-bold">Clinical escalation</h4>
					</Link>
					{data.observations && (
						<InputRTE
							readOnly
							minRows={1}
							aria-label="Observations"
							variant="borderless"
							className="p-0"
							value={data.observations}
						/>
					)}
				</div>
				<div className="flex flex-wrap gap-2">
					{data.labels.map((label) => (
						<Tag variant="filled" key={label} className="m-0">
							{humanReadable(label)}
						</Tag>
					))}
				</div>

				{data.linkedScreenings.length > 0 && (
					<ul
						className="flex flex-wrap gap-3"
						aria-label={`Escalation has ${data.linkedScreenings.length} related screening(s)`}
					>
						{data.linkedScreenings.map(
							({ id, capturedAt, imageThumbS3Key }) => {
								const weekday = dateFormatter(capturedAt, {
									onlyWeekday: true,
									needSimplify: true,
								})
								const timestamp = dateFormatter(capturedAt, {
									hasYear: false,
								})
								return (
									<li
										key={id}
										aria-level={2}
										aria-label={`Escalation screening at ${timestamp}`}
									>
										<button
											type="button"
											className="focus-outline flex cursor-pointer gap-3 rounded-xl border border-mint-20 p-3 text-left"
											onClick={() => {
												navigateToScreening(id)
											}}
										>
											{!imageThumbS3Key && (
												<div className="flex items-center justify-center">
													<WeightIcon.Null className="size-11 text-navy-60" />
												</div>
											)}

											{imageThumbS3Key && (
												<Image
													src={imageThumbS3Key}
													alt={`Screening image at ${timestamp}`}
													className="size-11 rounded-xl bg-grey-30 object-cover"
												/>
											)}
											<div className="flex flex-col">
												<div className="text-lg font-bold">{weekday}</div>
												<div className="text-navy-60">{timestamp}</div>
											</div>
										</button>
									</li>
								)
							},
						)}
					</ul>
				)}

				<Divider variant="dashed" className="my-0 border-grey-20" />

				<div className="flex flex-wrap items-center justify-end gap-1">
					<Tag variant="filled" color={isOpen ? "blue" : "default"}>
						{isOpen ? "Open" : "Closed"}
					</Tag>
					<Divider vertical />
					<Footnote
						editedBy={`${name}, ${date.fromNow()}`}
						editedByTooltip={dateFormatter(date.toISOString(), {
							hasYear: false,
						})}
					/>
				</div>
			</div>
		</div>
	)
}

function getItemId(item: TimelineItem): string {
	if (item.itemType === "timelineNote" && item.data?.id) {
		return item.data.id
	}
	if (item.itemType === "screening" && item.data?.id) {
		return item.data.id
	}
	if (item.itemType === "deviceEvent" && item.data?.visitId) {
		return item.data.visitId
	}
	if (item.itemType === "escalation" && item.data?.id) {
		return item.data.id
	}
	return `${item.itemType}-${item.effectiveDate}`
}

function midwayDate(
	prevDate?: string | null,
	nextDate?: string | null,
	fallbackDate?: string,
): string {
	if (prevDate && nextDate) {
		const prevMs = asDate(prevDate).valueOf()
		const nextMs = asDate(nextDate).valueOf()
		return asDate(Math.round((prevMs + nextMs) / 2)).toISOString()
	}

	if (prevDate) {
		return asDate(prevDate).subtract(1, "minute").toISOString()
	}

	if (nextDate) {
		return asDate(nextDate).add(1, "minute").toISOString()
	}

	return fallbackDate ?? asDate().toISOString()
}
